import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '@/lib/queryClient';
import { clearUsername } from '@/utils/analytics';
import { postAuthLogoutOptions } from '@/services/queryOptions/authQueryOptions';
import {
  authKeys,
  pointsKeys,
  questKeys,
  traitsKeys,
  nftKeys,
  faucetKeys
} from '@/constant/queryKeys';

export type OAuthProvider = 'x' | 'google';

export interface LinkedProvider {
  provider: OAuthProvider;
  providerEmail: string | null;
  isPrimary: boolean;
  linkedAt: string;
}

export interface User {
  id: number;
  name: string;
  username: string;
  profileImage: string;
  linkedProviders?: LinkedProvider[];
}

const userScopedQueryKeys = [
  authKeys.all,
  ['wallet'],
  pointsKeys.userPoints(),
  questKeys.getWithUserStatusById(),
  traitsKeys.traitsOwnedByUser(),
  nftKeys.userCollection,
  faucetKeys.all,
];

function isUserScopedQueryKey(queryKey: readonly unknown[]) {
  const [scope] = queryKey;

  if (
    scope === 'auth' ||
    scope === 'wallet' ||
    scope === 'points' ||
    scope === 'quests' ||
    scope === 'faucet'
  ) {
    return true;
  }

  if (scope === 'traits' && queryKey.includes('user-owned')) {
    return true;
  }

  if (scope === 'nfts' && queryKey.includes('user-collection')) {
    return true;
  }

  return typeof scope === 'string' && scope.startsWith('/api/v1/wallet');
}

async function clearUserScopedQueries() {
  await Promise.all(
    userScopedQueryKeys.map((queryKey) =>
      queryClient.cancelQueries({ queryKey })
    )
  );
  await queryClient.cancelQueries({ predicate: (query) => isUserScopedQueryKey(query.queryKey) });

  userScopedQueryKeys.forEach((queryKey) => {
    queryClient.removeQueries({ queryKey });
  });
  queryClient.removeQueries({ predicate: (query) => isUserScopedQueryKey(query.queryKey) });
}

interface AuthState {
  isAuthenticated: boolean;
  sessionValidated: boolean;
  user: User | null;
  login: (userData: User) => void;
  markUnauthenticatedSessionValidated: () => void;
  logout: (options?: { remote?: boolean }) => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      sessionValidated: false,
      user: null,
      login: (userData: User) => set({
        sessionValidated: true,
        isAuthenticated: true,
        user: userData,
      }),
      markUnauthenticatedSessionValidated: () => set({
        sessionValidated: true,
        isAuthenticated: false,
        user: null,
      }),
      logout: async (options) => {
        const shouldCallRemote = options?.remote !== false && get().isAuthenticated;

        if (shouldCallRemote) {
          try {
            // Call backend to clear httpOnly cookie
            const mutationOptions = postAuthLogoutOptions();
            await queryClient.getMutationCache().build(queryClient, mutationOptions).execute();
          } catch (error) {
            // Continue with logout even if API call fails
            console.error('Logout API call failed:', error);
          }
        }

        // Clear analytics username
        clearUsername();

        // Clear local auth state
        set({
          sessionValidated: true,
          isAuthenticated: false,
          user: null,
        });

        await clearUserScopedQueries();
      },
      updateUser: (updates: Partial<User>) => set((state) => ({
        user: state.user ? { ...state.user, ...updates } : null,
      })),
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        // Don't persist sessionValidated - it should be revalidated on each app load
      }),
    }
  )
);
